// /badges-admin: the badge engine switch, the sweep (dry run, then for real),
// everyone's badge rank, the whole catalogue with who holds what, and the
// legacy upload tools.
import React, { useCallback, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useGetAdminBadgeDefsQuery } from "./adminBadgesApi";
import { resolveAdminBadge } from "./adminModel";
import type { AdminBadge } from "./adminTypes";
import EngineStatusPanel from "./EngineStatusPanel";
import SweepPanel from "./SweepPanel";
import RankingPanel from "./RankingPanel";
import CataloguePanel from "./CataloguePanel";
import BadgeAdminDetailSheet from "./BadgeAdminDetail";
import LegacyTools from "./LegacyTools";
import "./badgeAdmin.css";

const EMPTY: AdminBadge[] = [];

const BadgeAdminPage: React.FC = () => {
  const { data, isLoading, isError, refetch } = useGetAdminBadgeDefsQuery();
  const badges = useMemo(() => (Array.isArray(data) ? data.map(resolveAdminBadge) : EMPTY), [data]);
  const byCode = useMemo(() => new Map(badges.map((b) => [b.code, b])), [badges]);

  // ?badge=<code> keeps the open panel in the URL, like the collection page
  const [params, setParams] = useSearchParams();
  const openCode = params.get("badge");
  const open = openCode ? byCode.get(openCode) ?? null : null;

  useEffect(() => {
    document.title = "NΛSOS Badge admin";
  }, []);

  const openBadge = useCallback(
    (code: string) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("badge", code);
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );
  const closeBadge = useCallback(
    (isOpen: boolean) => {
      if (isOpen) return;
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("badge");
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  return (
    <div className="badm-page">
      <div className="badm-page__inner">
        <header className="badm-page__head">
          <div>
            <p className="badm-page__kicker">
              <Link to="/admin">Admin</Link> / badges
            </p>
            <h1 className="badm-page__title">Badges</h1>
          </div>
          <Link className="badm-btn badm-btn--sm" to="/badges">
            Your collection
          </Link>
        </header>

        <EngineStatusPanel />
        <SweepPanel defs={byCode} />
        <RankingPanel />
        <CataloguePanel
          badges={badges}
          loading={isLoading}
          error={isError}
          onRetry={() => refetch()}
          onOpen={openBadge}
        />
        <LegacyTools badges={badges} />
      </div>

      <BadgeAdminDetailSheet badge={open} open={!!open} onOpenChange={closeBadge} />
    </div>
  );
};

export default BadgeAdminPage;
